import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Plus, CheckSquare, Boxes } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  CollaboratorInfoCard,
  ActiveAssignmentsSection,
  RentalSection,
  HistorySection,
  AssignToolDialog,
  ReplaceToolDialog,
  AssignRentalDialog,
  MultiAssignDialog,
  KitAssignDialog,
} from '@/components/collaborator';

const CollaboratorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [tools, setTools] = useState([]);
  const [kits, setKits] = useState([]);
  const [rentalEquipment, setRentalEquipment] = useState([]);
  const [collaboratorRentals, setCollaboratorRentals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog visibility
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [multiAssignDialogOpen, setMultiAssignDialogOpen] = useState(false);
  const [kitAssignDialogOpen, setKitAssignDialogOpen] = useState(false);
  const [assignRentalDialogOpen, setAssignRentalDialogOpen] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  // Confirm dialogs
  const [confirmReturnOpen, setConfirmReturnOpen] = useState(false);
  const [confirmReturnRentalOpen, setConfirmReturnRentalOpen] = useState(false);
  const [returnAssignmentId, setReturnAssignmentId] = useState(null);
  const [returnRentalId, setReturnRentalId] = useState(null);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const requests = [
        axios.get(`${API}/collaborators/${id}/assignments`),
        axios.get(`${API}/tools`),
      ];
      if (user?.role === 'admin') {
        requests.push(axios.get(`${API}/rentals/equipment`));
        requests.push(axios.get(`${API}/rentals/collaborator/${id}`));
        requests.push(axios.get(`${API}/kits`));
      }
      const results = await Promise.all(requests);
      setData(results[0].data);
      setTools(results[1].data.filter(t => t.quantity_available > 0));
      if (user?.role === 'admin') {
        setRentalEquipment(results[2]?.data?.filter(e => e.quantity_available > 0) || []);
        setCollaboratorRentals(results[3]?.data || []);
        setKits(results[4]?.data || []);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      navigate('/collaborators');
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers ---
  const handleAssign = async (form) => {
    try {
      let rentalDurationDays = null;
      if (form.duration_type === 'temporary' && form.end_date) {
        rentalDurationDays = Math.max(1, Math.ceil((new Date(form.end_date) - new Date()) / (1000 * 60 * 60 * 24)));
      }
      await axios.post(`${API}/assignments`, {
        tool_id: form.tool_id, collaborator_id: id, quantity: parseInt(form.quantity),
        rental_duration_days: rentalDurationDays, assignment_type: form.assignment_type,
        replacement_reason: form.assignment_type === 'replacement' ? form.replacement_reason : null,
        old_tool_id: form.assignment_type === 'replacement' ? form.old_tool_id : null,
      });
      toast.success('Outil affecté avec succès');
      setAssignDialogOpen(false);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Une erreur est survenue'); }
  };

  const handleReplace = async (form, assignment) => {
    try {
      let rentalDurationDays = null;
      if (form.duration_type === 'temporary' && form.end_date) {
        rentalDurationDays = Math.max(1, Math.ceil((new Date(form.end_date) - new Date()) / (1000 * 60 * 60 * 24)));
      }
      await axios.post(`${API}/assignments/replace`, {
        old_assignment_id: assignment.id, new_tool_id: form.new_tool_id,
        quantity: parseInt(form.quantity), reason: form.reason, rental_duration_days: rentalDurationDays,
      });
      toast.success('Outil remplacé avec succès');
      setReplaceDialogOpen(false);
      setSelectedAssignment(null);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Une erreur est survenue'); }
  };

  const handleReturn = (assignmentId) => { setReturnAssignmentId(assignmentId); setConfirmReturnOpen(true); };

  const confirmReturn = async () => {
    if (!returnAssignmentId) return;
    try {
      await axios.post(`${API}/assignments/${returnAssignmentId}/return`);
      toast.success('Outil retourné avec succès');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors du retour'); }
    finally { setConfirmReturnOpen(false); setReturnAssignmentId(null); }
  };

  const handleReturnRental = (rentalId) => { setReturnRentalId(rentalId); setConfirmReturnRentalOpen(true); };

  const confirmReturnRental = async () => {
    if (!returnRentalId) return;
    try {
      await axios.post(`${API}/rentals/return/${returnRentalId}`);
      toast.success('Équipement de location retourné avec succès');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors du retour'); }
    finally { setConfirmReturnRentalOpen(false); setReturnRentalId(null); }
  };

  const handleMultiAssign = async (selectedTools, durationType, endDate) => {
    const ids = Object.keys(selectedTools);
    if (ids.length === 0) { toast.error('Veuillez sélectionner au moins un outil'); return; }
    try {
      let successCount = 0, errorCount = 0;
      for (const toolId of ids) {
        try {
          await axios.post(`${API}/assignments`, {
            tool_id: toolId, collaborator_id: id, quantity: selectedTools[toolId].quantity,
            duration_type: durationType, end_date: durationType === 'temporary' ? endDate : null, assignment_type: 'new',
          });
          successCount++;
        } catch { errorCount++; }
      }
      if (successCount > 0) toast.success(`${successCount} outil(s) affecté(s) avec succès`);
      if (errorCount > 0) toast.error(`${errorCount} erreur(s) lors de l'affectation`);
      setMultiAssignDialogOpen(false);
      loadData();
    } catch { toast.error('Une erreur est survenue'); }
  };

  const handleKitAssign = async (kitId, durationType, endDate) => {
    if (!kitId) { toast.error('Veuillez sélectionner un kit'); return; }
    try {
      await axios.post(`${API}/kits/assign`, {
        kit_id: kitId, collaborator_id: id,
        duration_type: durationType, end_date: durationType === 'temporary' ? endDate : null,
      });
      const kit = kits.find(k => k.id === kitId);
      toast.success(`Kit "${kit?.name}" affecté avec succès`);
      setKitAssignDialogOpen(false);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Une erreur est survenue'); }
  };

  const handleAssignRental = async (form) => {
    if (!form.equipment_id) return;
    try {
      await axios.post(`${API}/rentals/assign`, {
        equipment_id: form.equipment_id, collaborator_id: id,
        quantity: parseInt(form.quantity), expected_return_date: form.expected_return_date || null,
      });
      toast.success('Équipement de location assigné avec succès');
      setAssignRentalDialogOpen(false);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Une erreur est survenue'); }
  };

  const openReplaceDialog = (assignment) => { setSelectedAssignment(assignment); setReplaceDialogOpen(true); };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg font-medium text-muted-foreground">Chargement...</div></div>;
  }

  const { collaborator, active_assignments: activeAssignments, history } = data;
  const isAdmin = user?.role === 'admin';

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button data-testid="back-button" onClick={() => navigate('/collaborators')} variant="outline" size="icon" className="rounded-full">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">{collaborator.name}</h1>
            <p className="text-muted-foreground mt-2">Détails et historique complet</p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button data-testid="kit-assign-button" onClick={() => setKitAssignDialogOpen(true)} variant="outline" className="rounded-full" disabled={kits.length === 0}>
              <Boxes className="w-4 h-4 mr-2" /> Affecter un Kit
            </Button>
            <Button data-testid="multi-assign-button" onClick={() => setMultiAssignDialogOpen(true)} variant="outline" className="rounded-full">
              <CheckSquare className="w-4 h-4 mr-2" /> Attribution Multiple
            </Button>
            <Button data-testid="add-assignment-button" onClick={() => setAssignDialogOpen(true)} className="rounded-full shadow-lg shadow-primary/20">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle Affectation
            </Button>
          </div>
        )}
      </div>

      <CollaboratorInfoCard collaborator={collaborator} API={API} onUserCreated={loadData} />
      <ActiveAssignmentsSection assignments={activeAssignments} isAdmin={isAdmin} onReturn={handleReturn} onReplace={openReplaceDialog} />
      {isAdmin && <RentalSection rentals={collaboratorRentals} onAssign={() => setAssignRentalDialogOpen(true)} onReturn={handleReturnRental} />}
      <HistorySection history={history} />

      {/* Dialogs */}
      <AssignToolDialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen} tools={tools} onSubmit={handleAssign} />
      <ReplaceToolDialog open={replaceDialogOpen} onOpenChange={setReplaceDialogOpen} tools={tools} selectedAssignment={selectedAssignment} onSubmit={handleReplace} />
      <AssignRentalDialog open={assignRentalDialogOpen} onOpenChange={setAssignRentalDialogOpen} collaboratorName={collaborator.name} rentalEquipment={rentalEquipment} onSubmit={handleAssignRental} />
      <MultiAssignDialog open={multiAssignDialogOpen} onOpenChange={setMultiAssignDialogOpen} tools={tools} onSubmit={handleMultiAssign} />
      <KitAssignDialog open={kitAssignDialogOpen} onOpenChange={setKitAssignDialogOpen} collaboratorName={collaborator.name} kits={kits} onSubmit={handleKitAssign} />

      <ConfirmDialog open={confirmReturnOpen} onOpenChange={setConfirmReturnOpen} onConfirm={confirmReturn}
        title="Retourner l'outil" description="Êtes-vous sûr de vouloir retourner cet outil ? Il sera ajouté au stock disponible et retiré des outils actifs du collaborateur." />
      <ConfirmDialog open={confirmReturnRentalOpen} onOpenChange={setConfirmReturnRentalOpen} onConfirm={confirmReturnRental}
        title="Retourner l'équipement de location" description="Êtes-vous sûr de vouloir retourner cet équipement de location ? Il sera de nouveau disponible pour les autres utilisateurs." />
    </div>
  );
};

export default CollaboratorDetail;
